import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { 
  Trophy, 
  Calendar as CalendarIcon,
  Users,
  Clock,
  Crown,
  Medal,
  TreeStructure,
  Table as TableIcon,
  ShareNetwork,
  WhatsappLogo,
  CaretRight,
  X,
  Lightning,
  UserCirclePlus,
  FilePdf
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  BracketView,
  LeagueStandingsView,
  LeagueFixturesView,
  ScheduledMatchesView,
} from "@/components/tournament/TournamentsSubViews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Calendar modifiers config — stable reference to prevent re-renders of react-day-picker
const CALENDAR_MODIFIERS_STYLES = {
  tournament: {
    backgroundColor: 'hsl(150, 60%, 45%)',
    color: 'hsl(0, 0%, 9%)',
    borderRadius: '50%',
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function Tournaments() {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [bracket, setBracket] = useState(null);
  const [leagueStandings, setLeagueStandings] = useState([]);
  const [leagueFixtures, setLeagueFixtures] = useState([]);
  const [scheduledMatches, setScheduledMatches] = useState([]);
  const [tournamentMatches, setTournamentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [detailDialog, setDetailDialog] = useState(false);
  const [liveMatches, setLiveMatches] = useState([]);

  const fetchLiveMatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/matches/live`);
      setLiveMatches(res.data || []);
    } catch (error) { console.error("Error fetching live matches:", error); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTournaments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tournaments`);
      setTournaments(response.data);
    } catch (error) {
      console.error("Error fetching tournaments:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScheduledMatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/matches/scheduled`);
      setScheduledMatches(res.data);
    } catch (error) {
      console.error("Error fetching scheduled matches:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTournaments();
    fetchScheduledMatches();
    fetchLiveMatches();
    const interval = setInterval(fetchLiveMatches, 10000);
    return () => clearInterval(interval);
  }, [fetchTournaments, fetchScheduledMatches, fetchLiveMatches]);

  const handleRegister = async (tournamentId) => {
    try {
      await axios.post(`${API}/tournaments/${tournamentId}/register`, {}, { withCredentials: true });
      toast.success("Registered successfully!");
      fetchTournamentDetails(selectedTournament);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
  };

  const fetchTournamentDetails = async (tournament) => {
    setSelectedTournament(tournament);
    setDetailDialog(true);
    setBracket(null);
    setLeagueStandings([]);
    setLeagueFixtures([]);
    setPlacements([]);
    setTournamentMatches([]);

    try {
      // Always fetch tournament matches
      const matchesRes = await axios.get(`${API}/tournaments/${tournament.id}/matches`);
      setTournamentMatches(matchesRes.data || []);

      if (tournament.status === 'completed') {
        const res = await axios.get(`${API}/tournaments/${tournament.id}/placements`);
        setPlacements(res.data);
      }

      if (tournament.tournament_format === 'knockout' || tournament.tournament_format === 'groups_knockout') {
        const res = await axios.get(`${API}/tournaments/${tournament.id}/bracket`);
        if (res.data && res.data.rounds) {
          setBracket(res.data);
        }
      }

      if (tournament.tournament_format === 'league') {
        const [standingsRes, fixturesRes] = await Promise.all([
          axios.get(`${API}/tournaments/${tournament.id}/league-standings`),
          axios.get(`${API}/tournaments/${tournament.id}/league-fixtures`)
        ]);
        setLeagueStandings(standingsRes.data);
        setLeagueFixtures(fixturesRes.data);
      }
    } catch (error) {
      console.error("Error fetching tournament details:", error);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const getTournamentBadgeClass = (type) => {
    switch (type) {
      case 'major': return 'badge-major';
      case 'monthly': return 'badge-monthly';
      default: return 'badge-weekly';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-primary/20 text-primary';
      case 'ongoing': return 'bg-secondary/20 text-secondary';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPointsForPosition = (type, position) => {
    const basePoints = { winner: 1000, runner_up: 700, semi_final: 400, quarter_final: 200 };
    const multipliers = { major: 1.5, monthly: 1.0, weekly: 0.5 };
    return Math.round(basePoints[position] * multipliers[type]);
  };

  const upcomingTournaments = tournaments.filter(t => t.status === 'upcoming');
  const ongoingTournaments = tournaments.filter(t => t.status === 'ongoing');
  const completedTournaments = tournaments.filter(t => t.status === 'completed');
  const tournamentDates = useMemo(() => tournaments.map(t => new Date(t.date)), [tournaments]);
  const calendarModifiers = useMemo(() => ({ tournament: tournamentDates }), [tournamentDates]);

  /** Streams the report PDF directly to a download — same UX as the
   *  Tournament Detail page's Export Report button. */
  const downloadReportPdf = async (t) => {
    if (!t?.id) return;
    const safeName = (t.name || "tournament").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 60);
    const tid = toast.loading("Preparing report PDF…");
    try {
      const res = await axios.get(`${API}/tournaments/${t.id}/export/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}_report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded", { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate report", { id: tid });
    }
  };

  const TournamentCard = ({ tournament }) => {
    const tournamentLiveMatches = liveMatches.filter(m => m.tournament_id === tournament.id);
    return (
    <motion.div variants={cardVariants}>
      <Link to={`/tournaments/${tournament.slug || tournament.id}`}>
        <Card 
          className={`tournament-card ${tournament.tournament_type} cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5`}
          data-testid={`tournament-card-${tournament.id}`}
        >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getTournamentBadgeClass(tournament.tournament_type)}`}>
                  {tournament.tournament_type.charAt(0).toUpperCase() + tournament.tournament_type.slice(1)}
                </span>
                <Badge className={getStatusColor(tournament.status)}>
                  {tournament.status.charAt(0).toUpperCase() + tournament.status.slice(1)}
                </Badge>
                <Badge variant="outline" className="text-xs capitalize">
                  {tournament.tournament_format?.replace(/_/g, ' ')}
                </Badge>
                {tournament.year && (
                  <Badge variant="outline" className="text-xs">{tournament.year}</Badge>
                )}
                {tournament.registration_open && tournament.status === 'upcoming' && (
                  <Badge variant="outline" className="text-xs border-primary/30 text-primary">Open Registration</Badge>
                )}
              </div>
              <h3 className="font-heading text-xl font-bold">{tournament.name}</h3>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CalendarIcon className="w-4 h-4" />
                  {formatDate(tournament.date)}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {tournament.registered_players?.length || 0} players
                </span>
              </div>
              {tournament.description && (
                <p className="mt-2 text-sm text-muted-foreground">{tournament.description}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Winner Prize</p>
              <p className="font-heading font-bold text-xl text-secondary">
                {getPointsForPosition(tournament.tournament_type, 'winner')} pts
              </p>
              {(tournament.status === 'completed' || tournament.status === 'ongoing') && (
                <Button
                  variant="outline" size="sm"
                  className="mt-2 h-7 px-2 text-xs border-primary/40 text-primary"
                  data-testid={`export-report-btn-${tournament.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    downloadReportPdf(tournament);
                  }}
                >
                  <FilePdf className="w-3.5 h-3.5 mr-1" />Report
                </Button>
              )}
            </div>
          </div>
          {/* Live scores for ongoing tournaments */}
          {tournamentLiveMatches.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border space-y-1.5">
              {tournamentLiveMatches.map(m => (
                <div key={m.id} className="flex items-center gap-2 text-sm bg-secondary/10 rounded-md px-3 py-1.5" data-testid={`card-live-${m.id}`}>
                  <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                  <span className="flex-1 text-right font-medium truncate">{m.player1_name || m.player1_id}</span>
                  <span className="font-heading font-bold px-2">{m.player1_score || 0} - {m.player2_score || 0}</span>
                  <span className="flex-1 font-medium truncate">{m.player2_name || m.player2_id}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </Link>
    </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
          <Trophy className="w-8 h-8 text-secondary" weight="fill" />
          Tournaments
        </h1>
        <p className="text-muted-foreground mt-1">
          Official tournament calendar, brackets, and results
        </p>
      </div>

      {/* Live Matches Banner */}
      {liveMatches.length > 0 && (
        <Card className="bg-card border-secondary/30 border-2">
          <CardContent className="p-4">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
              <Lightning className="w-5 h-5 text-secondary" weight="fill" />
              Live Now
            </h3>
            <div className="space-y-2">
              {liveMatches.map(m => (
                <a key={m.id} href={`/live/${m.id}`} className="flex items-center gap-4 p-3 rounded-lg bg-secondary/10 border border-secondary/20 hover:bg-secondary/15 transition-colors" data-testid={`live-match-${m.id}`}>
                  <span className="flex-1 text-right font-medium text-sm">{m.player1_name || m.player1_id}</span>
                  <div className="text-center px-3">
                    <span className="font-heading font-bold text-lg">{m.player1_score || 0} - {m.player2_score || 0}</span>
                    <div className="w-2 h-2 rounded-full bg-destructive animate-pulse mx-auto mt-1" />
                  </div>
                  <span className="flex-1 font-medium text-sm">{m.player2_name || m.player2_id}</span>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tournament List */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="ongoing" className="space-y-4">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="ongoing" data-testid="tab-ongoing">
                Ongoing ({ongoingTournaments.length})
              </TabsTrigger>
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">
                Upcoming ({upcomingTournaments.length})
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">
                Completed ({completedTournaments.length})
              </TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule">
                Schedule ({scheduledMatches.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ongoing">
              <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-4">
                {ongoingTournaments.length > 0 ? (
                  ongoingTournaments.map((t) => <TournamentCard key={t.id} tournament={t} />)
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No tournaments currently in progress</p>
                  </div>
                )}
              </motion.div>
            </TabsContent>

            <TabsContent value="upcoming">
              <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-4">
                {upcomingTournaments.length > 0 ? (
                  upcomingTournaments.map((t) => <TournamentCard key={t.id} tournament={t} />)
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No upcoming tournaments</p>
                  </div>
                )}
              </motion.div>
            </TabsContent>

            <TabsContent value="completed">
              <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-4">
                {completedTournaments.length > 0 ? (
                  completedTournaments.map((t) => <TournamentCard key={t.id} tournament={t} />)
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No completed tournaments</p>
                  </div>
                )}
              </motion.div>
            </TabsContent>

            <TabsContent value="schedule">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Upcoming Match Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScheduledMatchesView matches={scheduledMatches} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Calendar */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                Calendar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md"
                modifiers={calendarModifiers}
                modifiersStyles={CALENDAR_MODIFIERS_STYLES}
              />
            </CardContent>
          </Card>

          {/* Annual Tournament List - Dynamic from actual tournaments */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Annual Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {tournaments.map(t => (
                  <li key={t.id} className="flex items-center justify-between">
                    <span className="truncate mr-2">{t.name}</span>
                    <Badge className={`text-xs ${
                      t.tournament_type === 'major' ? 'badge-major' :
                      t.tournament_type === 'monthly' ? 'badge-monthly' :
                      'badge-weekly'
                    }`}>
                      {t.tournament_type?.charAt(0).toUpperCase() + t.tournament_type?.slice(1)}
                    </Badge>
                  </li>
                ))}
                {tournaments.length === 0 && (
                  <li className="text-muted-foreground text-center py-2">No tournaments this year</li>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tournament Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[85vh] overflow-y-auto">
          {selectedTournament && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-secondary" weight="fill" />
                  {selectedTournament.name}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getTournamentBadgeClass(selectedTournament.tournament_type)}`}>
                    {selectedTournament.tournament_type}
                  </span>
                  <Badge className={getStatusColor(selectedTournament.status)}>
                    {selectedTournament.status}
                  </Badge>
                  <Badge variant="outline" className="text-xs capitalize">
                    {selectedTournament.tournament_format?.replace(/_/g, ' ')}
                  </Badge>
                  <span className="text-sm text-muted-foreground ml-2">{formatDate(selectedTournament.date)}</span>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Registration Info for Upcoming */}
                {selectedTournament.status === 'upcoming' && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Registered Players ({selectedTournament.registered_players?.length || 0})
                    </h3>
                    {selectedTournament.registered_players?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {tournamentMatches.length === 0 && selectedTournament.registered_players.map((pid, i) => (
                          <Badge key={pid} variant="outline" className="text-sm py-1">{pid}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No players registered yet</p>
                    )}
                    {selectedTournament.registration_open && user && (
                      <Button onClick={() => handleRegister(selectedTournament.id)} className="mt-3" data-testid="register-tournament-btn">
                        <UserCirclePlus className="w-4 h-4 mr-2" />Register for Tournament
                      </Button>
                    )}
                    {selectedTournament.registration_open && !user && (
                      <p className="text-sm text-primary mt-2">Sign in and claim a player profile to register.</p>
                    )}
                  </div>
                )}

                {/* Schedule + Completed Matches for Ongoing/Completed */}
                {tournamentMatches.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary" />
                      Matches ({tournamentMatches.filter(m => m.status === 'completed').length} completed / {tournamentMatches.length} total)
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {tournamentMatches.map(m => (
                        <div key={m.id} className={`flex items-center gap-3 p-3 rounded-lg border ${
                          m.status === 'live' ? 'border-secondary/40 bg-secondary/10' :
                          m.status === 'completed' ? 'border-border bg-muted/20' : 'border-border/50 bg-card'
                        }`}>
                          <span className="flex-1 text-right text-sm font-medium">{m.player1_name}</span>
                          <div className="text-center min-w-[80px]">
                            {m.status === 'completed' || m.status === 'live' ? (
                              <span className="font-heading font-bold">{m.player1_score}-{m.player2_score}</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {m.scheduled_date || 'TBD'} {m.scheduled_time ? `${m.scheduled_time}` : ''}
                              </span>
                            )}
                            {m.table_name && (
                              <span className="text-[10px] text-secondary font-medium block" data-testid={`tournament-match-table-${m.id}`}>
                                {m.table_name}
                              </span>
                            )}
                            {m.status === 'live' && <div className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse mx-auto mt-1" />}
                            {m.is_walkover && <span className="text-[10px] text-muted-foreground block">W/O</span>}
                          </div>
                          <span className="flex-1 text-sm font-medium">{m.player2_name}</span>
                          <Badge variant="outline" className="text-[10px] capitalize">{m.round?.replace(/_/g, ' ')}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bracket View */}
                {bracket && bracket.rounds && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <TreeStructure className="w-5 h-5 text-primary" />
                      Tournament Bracket
                    </h3>
                    <BracketView bracket={bracket} />
                  </div>
                )}

                {/* League Standings */}
                {leagueStandings.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <TableIcon className="w-5 h-5 text-primary" />
                      League Standings
                    </h3>
                    <LeagueStandingsView standings={leagueStandings} />
                  </div>
                )}

                {/* League Fixtures */}
                {leagueFixtures.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-secondary" />
                      Fixtures
                    </h3>
                    <LeagueFixturesView fixtures={leagueFixtures} />
                  </div>
                )}

                {/* Placements */}
                {placements.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Medal className="w-5 h-5 text-secondary" weight="fill" />
                      Results
                    </h3>
                    <div className="space-y-3">
                      {placements.map((placement, index) => (
                        <div key={placement.id || index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            placement.position === 'winner' ? 'rank-1' :
                            placement.position === 'runner_up' ? 'rank-2' :
                            placement.position === 'semi_final' ? 'rank-3' : 'bg-muted'
                          }`}>
                            {placement.position === 'winner' && <Crown className="w-5 h-5" weight="fill" />}
                            {placement.position === 'runner_up' && <Medal className="w-5 h-5" weight="fill" />}
                            {placement.position === 'semi_final' && '3'}
                            {placement.position === 'quarter_final' && '5'}
                          </div>
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={placement.player_photo} />
                            <AvatarFallback className="text-xs">{getInitials(placement.player_name)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium">{placement.player_name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{placement.position.replace('_', ' ')}</p>
                          </div>
                          <span className="font-heading font-bold text-primary">+{placement.points_earned}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* YouTube embed */}
                {selectedTournament.youtube_live_url && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3">Live Stream</h3>
                    <div className="aspect-video rounded-lg overflow-hidden">
                      <iframe
                        src={selectedTournament.youtube_live_url.replace('watch?v=', 'embed/')}
                        className="w-full h-full"
                        allowFullScreen
                        title="Live Stream"
                      />
                    </div>
                  </div>
                )}

                {/* No details fallback */}
                {!bracket && leagueStandings.length === 0 && placements.length === 0 && !selectedTournament.youtube_live_url && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No additional details available yet</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
